import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { RegisterBatch } from './RegisterBatch'
import { useBatchStatus, useBatchDeregister } from './hooks'
import type { BatchJob } from './types'
import { curriculumUrl } from './utility'

export function AiTreeToolBatch() {
  const { status, isLoading, error, refetch } = useBatchStatus()
  const [removingKey, setRemovingKey] = useState<string | null>(null)
  const {
    deregister,
    isLoading: isDeregistering,
    error: deregisterError,
  } = useBatchDeregister(refetch)

  const handleRemove = useCallback(
    async (job: BatchJob) => {
      const key = `${job.tree_id}:${job.tree_ver}`
      setRemovingKey(key)
      try {
        await deregister({ tree_id: job.tree_id, tree_ver: job.tree_ver })
      } finally {
        setRemovingKey(null)
      }
    },
    [deregister],
  )

  useEffect(() => {
    refetch()
  }, [refetch])

  if (isLoading && !status) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={24} />
        <Typography>Loading batch status...</Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Typography color="error">
        Failed to load batch status: {error}
      </Typography>
    )
  }

  if (status?.state !== 'registering') {
    return null
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        AI Tree Tool Batch Processing
      </Typography>
      <RegisterBatch onSuccess={refetch} />
      {status.jobs.length > 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Registered Jobs ({status.jobs.length})
          </Typography>
          {deregisterError && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {deregisterError}
            </Typography>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Curriculum</TableCell>
                  <TableCell>Prompt ID</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {status.jobs.map((job) => (
                  <TableRow key={`${job.tree_id}:${job.tree_ver}`}>
                    <TableCell>{job.title}</TableCell>
                    <TableCell>
                      <Link
                        href={curriculumUrl(job.tree_id, job.tree_ver)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {curriculumUrl(job.tree_id, job.tree_ver)}
                      </Link>
                    </TableCell>
                    <TableCell>{job.prompt_id}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRemove(job)}
                        disabled={isDeregistering}
                      >
                        {removingKey === `${job.tree_id}:${job.tree_ver}`
                          ? 'Removing...'
                          : 'Remove'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  )
}
